import { ResourceName, ValidAny } from "@/types";
import columnsMetadata from "@utils/helpers/allColumnsMetadata.json";
/**
 * Metadata format for each column field.
 */
export interface ColumnMetadata {
  label: string;
  tooltip: string;
}

/**
 * Props returned for rendering column-based UI elements.
 */
export interface ColumnProps {
  source: string;
  label: string;
  title: string;
  name: string;
  helperText?: string;
}

/**
 * Replaces all `{{key}}` placeholders in a string with values from a replacement object.
 *
 * @param text - Text containing `{{...}}` patterns.
 * @param replacement - Optional key-value map for dynamic insertion.
 * @returns Text with replacements applied.
 */
const replaceDoubleBraces = (text: string, replacement?: Record<string, ValidAny>): string => {
  if (!replacement) return text;

  return text.replace(/{{(.+?)}}/g, (_, key) => replacement[key] ?? "");
};

/**
 * Retrieves label and tooltip metadata for a given field key and record type.
 *
 * @param key - The metadata key (field name), typically matching the `source` used in components.
 * @param recordType - The resource type the field belongs to (e.g., ResourceName.CLAIMANTS, ResourceName.POLICIES).
 * @param textReplace - (Optional) A key-value object for replacing `{{...}}` placeholders in the metadata text.
 * @returns An object containing props used for rendering labels and tooltips in UI components.
 */
export function getProps(key: string, recordType?: ResourceName, textReplace?: Record<string, ValidAny>): ColumnProps {
  // If no recordType is provided use the key for shared definitions
  const recordMetadata = recordType ? columnsMetadata[recordType] : columnsMetadata[key];

  if (!recordMetadata || !recordMetadata[key]) {
    console.warn(`Metadata not found for key "${key}" under record type "${recordType}".`);
  }

  let metadata: ColumnMetadata = recordType ? recordMetadata?.[key] : recordMetadata;

  if (!metadata)
    metadata = {
      label: `Label missing for "${key}" in "${recordType}"`,
      tooltip: `Tooltip missing for "${key}" in "${recordType}"`,
    };

  return {
    source: key,
    label: replaceDoubleBraces(metadata.label, textReplace),
    title: replaceDoubleBraces(metadata.tooltip, textReplace),
    name: key,
  };
}

/**
 * Retrieves props for input components, using metadata for labels and tooltips.
 *
 * Unlike react-admin's `helperText`, this function does **not** automatically attach
 * tooltips beneath inputs (to avoid crowded forms). Instead, it exposes the tooltip
 * text as a separate `tooltip` property, which can be used in custom renderers
 * (e.g., hover labels, info icons, etc.).
 *
 * @param key - The metadata key (field name), typically matching the `source`.
 * @param textReplace - Optional key-value map for placeholder substitution in label/tooltip text.
 * @param recordType - The parent resource type (e.g., ResourceName.CLAIMANTS, ResourceName.POLICIES).
 * @returns ColumnProps including `label`, `title`, and an additional optional `tooltip` property.
 */
export function getInputProps(
  key: string,
  textReplace: Record<string, ValidAny> = {},
  recordType: ResourceName
): ColumnProps & { tooltip?: string } {
  const baseProps = getProps(key, recordType, textReplace);

  return {
    ...baseProps,
    tooltip: baseProps.title,
  };
}
